import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Apollo, gql } from 'apollo-angular';
import { marked } from 'marked';
import { MessageService } from 'primeng/api';
import { combineLatest, startWith, Subscription } from 'rxjs';
import { CommonService } from 'src/app/app-common/common.service';
import { CrudStateService, GqlRequestInfo, gqlRequestInfo } from 'src/app/crud-state.service';
import { ArrayImportOptions, FlagNodeImportOptions, FlagValueImportOptions, ScopeSnapshots } from 'src/generated/graphql';
import { TypedJSON } from 'typesafe-json';

// enum ImportJsonEmittableEvent {
// 	CANCEL = 'CANCEL',
// 	IMPORTED = 'IMPORTED'
// }

type ScopeSnapshot = Pick<ScopeSnapshots, 'id' | 'scopeId' | 'scopeVersion'>;

type JsonNodeType = 'object' | 'array' | 'value';

type ImportTypeIncompatibility = {
	path: (string | number)[];
	expectedType: JsonNodeType;
	providedType: JsonNodeType;
	providedValue: unknown;
};

type FlagNodeImportOption = NonNullable<FlagNodeImportOptions['name']>;
type FlagValueImportOption = NonNullable<FlagValueImportOptions['name']>;
type ArrayImportOption = NonNullable<ArrayImportOptions['name']>;

type ImportOptionsQuery = {
	flagNodeImportOptions: Pick<FlagNodeImportOptions, 'name' | 'description'>[],
	flagValueImportOptions: Pick<FlagValueImportOptions, 'name' | 'description'>[],
	arrayImportOptions: Pick<ArrayImportOptions, 'name' | 'description'>[]
};

type ImportConfigMutation = {
	importConfig: {
		flagNodeOperation: string | null,
		flagValueOperation: string | null
	}[]
};

type ImportConfigMutationVariables = {
	scopeSnapshotId: number,
	importConfig: {[key: string]: any},
	flagNodeImportOption: FlagNodeImportOption,
	flagValueImportOption: FlagValueImportOption,
	arrayImportOption: ArrayImportOption
};

type ComponentGqlRequestInfos = {
	queries: {
		importOptions: GqlRequestInfo<ImportOptionsQuery, Record<string, never>>
	},
	mutations: {
		importConfig: GqlRequestInfo<ImportConfigMutation, ImportConfigMutationVariables>
	},
	fragments: {
	},
};

@Component({
  selector: 'app-import-json[scope-snapshot][scope-snapshot-json]',
  templateUrl: './import-json.component.html',
  styleUrls: ['./import-json.component.scss']
})
export class ImportJsonComponent implements OnInit, OnDestroy {
	@Input('scope-snapshot') scopeSnapshot?: ScopeSnapshot;
	@Input('scope-snapshot-json') scopeSnapshotJson!: any;
	@Output('imported') importedEvent: EventEmitter<void> = new EventEmitter();
	@Output('cancel') cancelEvent: EventEmitter<void> = new EventEmitter();

	flagNodeImportOptions: FlagNodeImportOption[] = [];
	flagValueImportOptions: FlagValueImportOption[] = [];
	arrayImportOptions: ArrayImportOption[] = [];

	private flagNodeImportOptionDescriptions = new Map<FlagNodeImportOption, string>();
	private flagValueImportOptionDescriptions = new Map<FlagValueImportOption, string>();
	private arrayImportOptionDescriptions = new Map<ArrayImportOption, string>();
	
	jsonInputIncompatibilities: ImportTypeIncompatibility[] | null = null;

	private jsonInputIncompatibilitiesSubscription?: Subscription;

	versionLabel = CommonService.friendlyVersionLabel;

	showingAdvancedOptions = false;

	constructor(private apollo: Apollo, private commonService: CommonService, private messageService: MessageService, private changeDetectorRef: ChangeDetectorRef) {}

	gqlRequestInfos: ComponentGqlRequestInfos = {
		queries: {
			importOptions: gqlRequestInfo(gql`
				query importOptions {
					flagNodeImportOptions(orderBy: {ordinal: ASC}) {
						name
						description
					}
					flagValueImportOptions(orderBy: {ordinal: ASC}) {
						name
						description
					}
					arrayImportOptions(orderBy: {ordinal: ASC}) {
						name
						description
					}
				}
			`)
		},
		mutations: {
			importConfig: gqlRequestInfo(gql`
				mutation importConfig(
					$scopeSnapshotId: Int!,
					$importConfig: jsonb!,
					$flagNodeImportOption: String!,
					$flagValueImportOption: String!,
					$arrayImportOption: String!
				) {
					importConfig(args: {
						scope_snapshot_id: $scopeSnapshotId,
						import_config: $importConfig,
						flag_node_import_option: $flagNodeImportOption,
						flag_value_import_option: $flagValueImportOption,
						array_import_option: $arrayImportOption
					}) {
						flagNodeOperation
						flagValueOperation
					}
				}
			`),
		},
		fragments: {}
	}

	importForm: FormGroup<{
		json: FormControl<string>,
		flagNodeImportOption: FormControl<FlagNodeImportOption>,
		flagValueImportOption: FormControl<FlagValueImportOption>,
		arrayImportOption: FormControl<ArrayImportOption>
	}> = new FormGroup({
		json: new FormControl<string>(
			"{}",
			{
				nonNullable: true,
				validators: [
					Validators.required,
					(formControl: AbstractControl) => {
						if (this.jsonInputToJsonObject(formControl.value) !== undefined) {
							return null;
						}
						else {
							return {invalidJson: true};
						}
					}
				]
			}
		),
		flagNodeImportOption: new FormControl<FlagNodeImportOption>(
			'CreateIfMissing',
			{nonNullable: true, validators: [Validators.required]}
		),
		flagValueImportOption: new FormControl<FlagValueImportOption>(
			'OnlyIfResultingInValueChange',
			{nonNullable: true, validators: [Validators.required]}
		),
		arrayImportOption: new FormControl<ArrayImportOption>(
			'MatchByIndex',
			{nonNullable: true, validators: [Validators.required]}
		)
	});

	ngOnInit() {
		const importOptionsInfo = this.gqlRequestInfos.queries.importOptions;
		importOptionsInfo.subscription?.unsubscribe();
		importOptionsInfo.subscription = this.apollo.query<ImportOptionsQuery>({
			query: importOptionsInfo.gql
		}).subscribe({
			next: ({ data }) => {
				this.flagNodeImportOptions = data.flagNodeImportOptions.map(option => option.name);
				this.flagValueImportOptions = data.flagValueImportOptions.map(option => option.name);
				this.arrayImportOptions = data.arrayImportOptions.map(option => option.name);

				this.flagNodeImportOptionDescriptions = new Map(
					data.flagNodeImportOptions.map(option => [option.name, option.description])
				);
				this.flagValueImportOptionDescriptions = new Map(
					data.flagValueImportOptions.map(option => [option.name, option.description])
				);
				this.arrayImportOptionDescriptions = new Map(
					data.arrayImportOptions.map(option => [option.name, option.description])
				);
			},
			error: (error) => this.commonService.queryErrorHandler(error)
		});

		this.jsonInputIncompatibilitiesSubscription = combineLatest([
			this.importForm.controls.json.valueChanges.pipe(startWith(this.importForm.controls.json.value)),
		
			this.importForm.controls.flagNodeImportOption.valueChanges.pipe(startWith(this.importForm.controls.flagNodeImportOption.value)),
		
			this.importForm.controls.flagValueImportOption.valueChanges.pipe(startWith(this.importForm.controls.flagValueImportOption.value)),
		
			this.importForm.controls.arrayImportOption.valueChanges.pipe(startWith(this.importForm.controls.arrayImportOption.value))
		]).subscribe(() => {
			this.updateJsonInputIncompatibilities();
		});
	}

	private updateJsonInputIncompatibilities(): void {
		const importingJson = this.jsonInputToJsonObject(
			this.importForm.controls.json.value
		);
	
		if (importingJson === undefined) {
			this.jsonInputIncompatibilities = null;
		}
		else {
			this.jsonInputIncompatibilities = this.findTypeIncompatibilities(this.scopeSnapshotJson, importingJson);
		}
		this.changeDetectorRef.detectChanges();
		return
	}

	flagNodeImportOptionLabel(flagNodeImportOption: FlagNodeImportOption): string {
		return this.importOptionDescriptionHtml(this.flagNodeImportOptionDescriptions.get(flagNodeImportOption), flagNodeImportOption);
	}

	flagValueImportOptionLabel(flagValueImportOption: FlagValueImportOption): string {
		return this.importOptionDescriptionHtml(this.flagValueImportOptionDescriptions.get(flagValueImportOption), flagValueImportOption);
	}

	arrayImportOptionLabel(arrayImportOption: ArrayImportOption): string {
		return this.importOptionDescriptionHtml(this.arrayImportOptionDescriptions.get(arrayImportOption), arrayImportOption);
	}

	private importOptionDescriptionHtml(description: string | undefined, fallback: string): string {
		return description === undefined ? fallback : marked.parseInline(description) as string;
	}

	jsonInputToJsonObject(jsonInput: string): {[key: string]: any;} | undefined {
		return TypedJSON.parse(jsonInput).object();
	}

	incompatibilitiesOfJsonInput(): ImportTypeIncompatibility[] | null {
		const importingJson = this.jsonInputToJsonObject(
			this.importForm.controls.json.value
		);
	
		if (importingJson === undefined) {
			return null;
		}
	
		return this.findTypeIncompatibilities(
			this.scopeSnapshotJson,
			importingJson
		);
	}

	public incompatibilitiesSectionTitle(): string | null {
		if (!this.jsonInputIncompatibilities?.length) {
			return null;
		}

		let flagBranchIncompatibilities = 0;
		let valueIncompatibilities = 0;

		for (const incompatibility of this.jsonInputIncompatibilities) {
			if (incompatibility.providedType === 'value') {
				valueIncompatibilities++;
			}

			if (['array', 'object'].includes(incompatibility.providedType)) {
				flagBranchIncompatibilities++;
			}
		}

		const parts: string[] = [];

		if (flagBranchIncompatibilities > 0) {
			parts.push(`${flagBranchIncompatibilities} Flag Branch${flagBranchIncompatibilities === 1 ? '' : 'es'}`);
		}

		if (valueIncompatibilities > 0) {
			parts.push(`${valueIncompatibilities} Value${valueIncompatibilities === 1 ? '' : 's'}`);
		}

		if (parts.length === 0) {
			return null;
		}

		return `Will Skip ${parts.join(' and ')} Due to Type Incompatibilities`;
	}

	private findTypeIncompatibilities(
		existingValue: unknown,
		importedValue: unknown,
		path: (string | number)[] = []
	): ImportTypeIncompatibility[] {
	
		const existingType = this.jsonNodeType(existingValue);
		const importedType = this.jsonNodeType(importedValue);
	
		const flagNodeImportOption = this.importForm.controls.flagNodeImportOption.value;
		const flagValueImportOption = this.importForm.controls.flagValueImportOption.value;
		const arrayImportOption = this.importForm.controls.arrayImportOption.value;
	
		if (existingType !== importedType) {
			if (
				importedType === 'value' &&
				flagValueImportOption === 'Skip'
			) {
				return [];
			}
	
			if (
				importedType !== 'value' &&
				flagNodeImportOption === 'Skip' &&
				flagValueImportOption === 'Skip'
			) {
				return [];
			}
	
			return [{
				path,
				expectedType: existingType,
				providedType: importedType,
				providedValue: importedValue
			}];
		}
	
		if (existingType === 'object') {
			const existingObject = existingValue as Record<string, unknown>;
			const importedObject = importedValue as Record<string, unknown>;
	
			return Object.entries(importedObject).flatMap(
				([propertyName, importedChildValue]) => {
					if (!Object.prototype.hasOwnProperty.call(existingObject, propertyName)) {
						return [];
					}
	
					return this.findTypeIncompatibilities(
						existingObject[propertyName],
						importedChildValue,
						[...path, propertyName]
					);
				}
			);
		}
	
		if (existingType === 'array') {
			if (arrayImportOption === 'Append') {
				return [];
			}
	
			const existingArray = existingValue as unknown[];
			const importedArray = importedValue as unknown[];
	
			const incompatibilities: ImportTypeIncompatibility[] = [];
	
			for (
				let index = 0;
				index < Math.min(existingArray.length, importedArray.length);
				index++
			) {
				incompatibilities.push(
					...this.findTypeIncompatibilities(
						existingArray[index],
						importedArray[index],
						[...path, index]
					)
				);
			}
	
			return incompatibilities;
		}
	
		return [];
	}
	
	private jsonNodeType(value: unknown): JsonNodeType {
		if (Array.isArray(value)) {
			return 'array';
		}
	
		if (value !== null && typeof value === 'object') {
			return 'object';
		}
	
		return 'value';
	}

	public incompatibilityPath(path: (string | number)[]): string {
		return path
			.map((part, index) => {
				if (typeof part === 'number') {
					return `[${part}]`;
				}

				return index === 0 ? part : `.${part}`;
			})
			.join('');
	}

	public incompatibilityValueType(value: unknown): string {
		if (Array.isArray(value)) {
			return 'Array';
		}

		if (value === null) {
			return 'Null';
		}

		if (typeof value === 'object') {
			return 'Object';
		}

		switch (typeof value) {
			case 'boolean':
				return 'Boolean';

			case 'number':
				return 'Number';

			case 'string':
				return 'String';

			default:
				return 'Value';
		}
	}

	public incompatibilityValue(value: unknown): string {
		return JSON.stringify(value);
	}


	cancelImport() {
		this.cancelEvent.emit();
	}

	requireScopeSnapshot(): ScopeSnapshot {
		if (this.scopeSnapshot === undefined) {
			throw new Error(`Scope snapshot is undefined.`);
		}
		return this.scopeSnapshot;
	}

	importJson() {
		if (this.importForm.valid) {
			const jsonInput = this.importForm.value.json;
			if (jsonInput === undefined) {
				throw new Error(`JSON input is undefined.`);
			}

			const parsedJsonObject = this.jsonInputToJsonObject(jsonInput);
			if (parsedJsonObject === undefined) {
				throw new Error(`JSON input is not an object.`);
			}

			const flagNodeImportOption = this.importForm.value.flagNodeImportOption;
			if (flagNodeImportOption === undefined) {
				throw new Error(`Flag node import option is undefined.`);
			}

			const flagValueImportOption = this.importForm.value.flagValueImportOption;
			if (flagValueImportOption === undefined) {
				throw new Error(`Flag value import option is undefined.`);
			}

			const arrayImportOption = this.importForm.value.arrayImportOption;
			if (arrayImportOption === undefined) {
				throw new Error(`Array import option is undefined.`);
			}

			const importConfigInfo = this.gqlRequestInfos.mutations.importConfig;
			importConfigInfo.subscription?.unsubscribe();
			importConfigInfo.subscription = this.apollo.mutate<ImportConfigMutation, ImportConfigMutationVariables>({
				mutation: importConfigInfo.gql,
				variables: {
					scopeSnapshotId: this.requireScopeSnapshot().id,
					importConfig: parsedJsonObject,
					flagNodeImportOption: flagNodeImportOption,
					flagValueImportOption: flagValueImportOption,
					arrayImportOption: arrayImportOption
				}
			}).subscribe({
				next: ({ data }) => {
					const importOperations = data?.importConfig ?? [];
					const importedFlagNodesCount = importOperations.filter(operation => operation.flagNodeOperation === 'CREATE_FLAG_NODE').length;
					const totalFlagNodesCount = importOperations.filter(operation => ['CREATE_FLAG_NODE', 'SKIP_FLAG_NODE_CREATION', 'SKIP_ANCESTOR_TYPE_MISMATCH', 'SKIP_TYPE_MISMATCH'].includes(operation.flagNodeOperation ?? '')).length;
					const importedFlagValuesCount = importOperations.filter(operation => operation.flagValueOperation === 'UPSERT_FLAG_VALUE').length;
					const totalFlagValuesCount = importOperations.filter(operation => operation.flagValueOperation !== null).length;
					const hasTypeIncompatibilities = importOperations.some(operation => ['SKIP_ANCESTOR_TYPE_MISMATCH', 'SKIP_TYPE_MISMATCH'].includes(operation.flagNodeOperation ?? ''));
					this.messageService.add({
						severity: 'success',
						summary: `Imported ${importedFlagNodesCount}/${totalFlagNodesCount} Flags, ${importedFlagValuesCount}/${totalFlagValuesCount} Values`,
						detail: `${hasTypeIncompatibilities ? 'Some flags and values were skipped due to type incompatibilities. ' : ''}See History entries for more details.`,
						life: 10000
					});
					this.importedEvent.emit();
				},
				error: (error) => this.commonService.mutationErrorHandler(error)
			});
		}
	}

	ngOnDestroy() {
		this.jsonInputIncompatibilitiesSubscription?.unsubscribe();

		CrudStateService.unsubscribeFromGqlSubscriptions(
			this.gqlRequestInfos
		);
	}

	// isValidJson() {
	// 	try {
	// 		JSON.parse(this.importForm.value.json);

	// setNewState(newState: ImportJsonSectionState) {
	// 	this.importJsonSection.state = newState;
	// 	this.emitState();
	// }

	// emitState() {
	// 	this.newStateEvent.emit(this.importJsonSection.state);
	// }

}